import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import z from "zod";
import { useTranslation } from "@vixi/localization";
import { initDefaultLogger, setZodErrorMap } from "@vixi/utils";

// Import the generated route tree
import { routerMap, RouterMapValue } from "./routes/routerMap";
import { RouterProvider, router, RouterIds } from "@vixi/router";

initDefaultLogger(import.meta.env);

// Zod error message customization
setZodErrorMap(z);

Object.entries(routerMap).forEach(([path, routerOptions]) => {
  const options = routerOptions as RouterMapValue;
  const foundRoute = router.routesById[path as RouterIds];

  foundRoute?.update({
    ...options,
  });
});

// React Query setup
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const ReactQueryDevtoolsProduction =
  import.meta.env.MODE === "staging"
    ? React.lazy(() =>
        import("@tanstack/react-query-devtools/build/modern/production.js").then((d) => ({
          default: d.ReactQueryDevtools,
        })),
      )
    : React.lazy(async () => ({ default: React.Fragment }));

export const App = () => {
  const { initTranslation } = useTranslation();

  useEffect(() => {
    initTranslation();
  }, []);

  // Show React Query Devtools in production if the environment variable is set
  const [showDevtools, setShowDevtools] = useState(false);

  useEffect(() => {
    // @ts-expect-error
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />

      <ReactQueryDevtools initialIsOpen={false} />
      {import.meta.env.MODE === "staging" && showDevtools && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </React.Suspense>
      )}
    </QueryClientProvider>
  );
};
